<template>
  <footer class="footer">
    <div class="footer__inner-wrapper">
      <div
        v-if="!compact"
        class="footer__top">
        <Link
          href="/"
          class="footer__logo hover"
          :style="{ '--offset': logoRightOffset }">
          <img
            :src="footerLogo"
            alt="company logo"
            :width="logoWidth"
            class="footer__logo-img">
        </Link>
        <ul
          class="footer__sections"
          :class="{ 'footer__sections_left': menuToLeft }">
          <li
            v-for="(section, index) in sections"
            :key="section.name"
            class="footer__section"
            :class="{ 'footer__section_prev': isPreviousElement(index) }">
            <Button
              variant="clear"
              size="sm"
              @click.self="toggleActive($event)"
              class="footer__section-title">
              <span v-html="section.name"></span>
              <Icon
                class="footer__section-arrow"
                name="down"
                size="s"/>
            </Button>
            <ul
              class="footer__section-items"
              :class="{ 'footer__section-items_prev': isPreviousElement(index) }">
              <li
                v-for="item in section.items"
                :key="item.name"
                class="footer__section-item">
                <Link
                  @click="handleItemClick($event, item)"
                  :href="item.href"
                  :target="getLinkTarget(item.href)"
                  class="footer__section-link hover">
                  <span v-html="item.name"></span>
                </Link>
              </li>
            </ul>
          </li>
          <li
            v-if="mobileAppData.href"
            class="footer__section1 footer__section_app">
            <MobileApp v-bind="mobileAppData" />
          </li>
        </ul>
      </div>
      <div class="footer__mobile">
        <Link
          href="/"
          class="footer__mobile-logo">
          <img
            :src="footerLogo"
            :width="logoWidth"
            alt="company logo"
            class="footer__mobile-logo-img">
        </Link>
        <div
          v-if="mobileAppData.href"
          class="footer__section">
          <MobileApp v-bind="mobileAppData" />
        </div>
      </div>
      <div
        v-if="links.length !== 0"
        class="footer__middle">
        <ul class="footer__middle-items">
          <li
            v-for="link in links"
            :key="link.title"
            class="footer__middle-item">
            <Link
              :target="getLinkTarget(link.href)"
              :href="link.href"
              class="footer__middle-link hover">
              <span v-html="link.text"></span>
            </Link>
          </li>
        </ul>
      </div>
      <div class="footer__bottom">
        <div class="footer__bottom-left">
          <span class="footer__bottom-year">
            © {{ year }}
          </span>
          <span
            v-if="copyright"
            class="footer__bottom-copyright"
            v-html="`, ${copyright}`">
          </span>
        </div>
        <div class="footer__bottom-right">
          <Button
            v-if="feedbackText.trim() !== ''"
            variant="clear"
            size="sm"
            class="footer__feedback hover"
            @click="$emit('feedback')">
            <Icon
              name="feedback"
              size="xs"/>
            <span
              v-html="feedbackText"
              class="footer__feedback-text"></span>
          </Button>
          <ul
            v-if="socials.length !== 0"
            class="footer__socials">
            <li
              v-for="social in socials"
              :key="social.icon"
              class="footer__social">
              <Link
                :href="social.href"
                target="_blank"
                class="footer__social-link">
                <Icon
                  size="l"
                  :name="social.icon"/>
              </Link>
            </li>
          </ul>
          <LangSwitch
            v-if="locales.length !== 0"
            @change="$emit('changeLang', $event)"
            :locales="locales"
            :initial-locale-code="currentLocale"/>
        </div>
      </div>
    </div>
  </footer>
</template>

<script>
import Icon from '@rr-component-library/icon/src/main';
import Button from '@rr-component-library/button/src/main';
import Link from '@rr-component-library/link/src/main';
import MobileApp from '../MobileApp/MobileApp.vue';
import LangSwitch from '../LangSwitch/LangSwitch.vue';

const defaultMobileApp = {
  title: 'Мобильное приложение',
  icon: require('../../assets/img/google-play-badge.svg'),
  href: 'https://play.google.com/store/apps/details?id=com.mirbega.russiarunning',
}

export default {
  name: 'Footer',
  designVersion: '1.2.4',
  components: {
    Icon,
    MobileApp,
    LangSwitch,
    Button,
    Link,
  },
  props: {
    /*
    * Разделы с навигацией
    * Array<{
    *   name: string,
    *   items: Array<{
    *     name: string,
    *     href: string,
    *     target?: string (default '_self')
    *   }>
    * }>
    */
    sections: {
      type: Array,
      required: true,
    },
     /*
      * Раздел с соцсетями
      * Array<{
      *   icon: string,
      *   href: string,
      * }>
      */
     socials: {
      type: Array,
      default: () => [],
     },
     /*
      * Разделы с ссылками
      * Array<{
      *   title: string,
      *   href: string,
      * }>
      */
     links: {
      type: Array,
      default: () => [],
     },
     compact: {
      type: Boolean,
      default: false,
     },
     copyright: {
      type: String,
      default: '',
     },
     logo: {
      type: String,
     },
     logoWidth: {
      type: [String, Number],
      default: 80,
     },
     logoRightOffset: {
      type: [String, Number],
      default: 130,
     },
      /*
      * Элемент мобильного приложения
      * {
      *   title: string,
      *   icon: string,
      *   href: string,
      * }
      */
     mobileApp: {
      type: Object,
     },
     locales: {
      type: Array,
      default: () => [],
     },
     currentLocale: {
      type: String,
      default: 'ru',
     },
     feedbackText: {
      type: String,
      default: '',
     },
  },
  data() {
    return {
      year: new Date().getFullYear(),
      mobileAppData: {
        ...defaultMobileApp,
        ...this.mobileApp
      },
    };
  },
  methods: {
    getLinkTarget(link = '') {
      const isAbsolute = link.startsWith('http') || link.startsWith('//');

      return isAbsolute ? '_blank' : '_self';
    },
    isPreviousElement(index) {
      return this.sections.length - 1 === index;
    },
    toggleActive(event) {
      event.target.classList.toggle('active');
    },
    handleItemClick(event, item) {
      if (!item.action) return true;

      event.preventDefault();
      item.action();
    }
  },
  computed: {
    footerLogo() {
      return this.logo || this.$options.defaultImages.footerLogo;
    },
    menuToLeft() {
      const maxColumns = 4;

      return this.sections.length < maxColumns;
    },
  },
  defaultImages: {
    footerLogo: require('../../assets/img/footer-logo.svg'),
  },
};
</script>

<style lang="scss" scoped>
  @import "./FooterBasic";
</style>
